import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Italic, Eyebrow } from "./_primitives";
import { CountUp } from "./_motion";
import {
  PORTFOLIO_COMPANIES,
  PORTFOLIO_STATS,
} from "../../data/sparkseed";

const SECTORS = ["All", "D2C", "Logistics", "SaaS", "Tech", "Manufacturing", "Fintech"];

const parseStat = (raw) => {
  const m = raw.match(/([^\d.]*)([\d.]+)(.*)/);
  if (!m) return { prefix: "", value: 0, suffix: raw, decimals: 0 };
  return {
    prefix: m[1] || "",
    value: parseFloat(m[2]),
    suffix: m[3] || "",
    decimals: m[2].includes(".") ? 1 : 0,
  };
};

export default function Portfolio() {
  const [filter, setFilter] = useState("All");
  const filtered = useMemo(
    () =>
      filter === "All"
        ? PORTFOLIO_COMPANIES
        : PORTFOLIO_COMPANIES.filter((c) => c.sector === filter),
    [filter]
  );

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="bg-[#F7F8EF] relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="max-w-3xl mb-10">
          <Eyebrow>Portfolio & Track Record</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-bold text-[#0F2818]">
            Our <Italic>Portfolio</Italic> & Track Record.
          </h2>
          <p className="mt-5 text-lg text-[#4A5A4F]">
            10+ investments. 1 acquisition. Counting.
          </p>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-12 rounded-3xl border border-[#14401F]/10 bg-white p-2">
          {PORTFOLIO_STATS.map((s, i) => {
            const p = parseStat(s.value);
            return (
            <div
              key={i}
              data-testid={`portfolio-stat-${i}`}
              className="text-center py-5 rounded-2xl hover:bg-[#DCE8C9]/50 transition-colors"
            >
              <div className="font-serif-display text-3xl lg:text-4xl font-bold text-[#14401F]">
                {p.value > 0 ? (
                  <CountUp
                    prefix={p.prefix}
                    value={p.value}
                    suffix={p.suffix}
                    decimals={p.decimals}
                  />
                ) : (
                  s.value
                )}
              </div>
              <div className="mt-1 text-xs uppercase tracking-[0.18em] text-[#4A5A4F] font-medium">
                {s.label}
              </div>
            </div>
            );
          })}
        </div>

        {/* Sector filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {SECTORS.map((s) => (
            <button
              key={s}
              data-testid={`sector-filter-${s.toLowerCase()}`}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                filter === s
                  ? "bg-[#14401F] text-white border-[#14401F]"
                  : "bg-transparent text-[#0F2818] border-[#14401F]/20 hover:border-[#14401F]/50"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Portfolio grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((c, i) => (
            <motion.div
              key={c.name}
              data-testid={`portfolio-card-${i}`}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: i * 0.05, duration: 0.4 }}
              className="group relative aspect-square sm:aspect-[4/3] [perspective:1000px]"
            >
              {/* Inner flipper */}
              <div className="relative w-full h-full transition-transform duration-[650ms] ease-[cubic-bezier(0.22,1,0.36,1)] [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
                {/* FRONT */}
                <div className="absolute inset-0 [backface-visibility:hidden] rounded-3xl border border-[#14401F]/10 bg-white flex flex-col items-center justify-center p-5 text-center shadow-sm">
                  {c.logo ? (
                    <div className="h-16 flex items-center justify-center mb-3">
                      <img
                        src={c.logo}
                        alt={c.name}
                        className="max-h-full max-w-[120px] w-auto object-contain"
                        style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.08))" }}
                      />
                    </div>
                  ) : (
                    <div className="h-12 w-12 rounded-2xl bg-[#DCE8C9] text-[#14401F] flex items-center justify-center font-serif-display font-bold text-lg mb-3">
                      {c.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                  )}
                  <div className="font-serif-display text-base font-bold text-[#0F2818] leading-tight">
                    {c.name}
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[#14401F] font-semibold">
                    {c.sector}
                  </div>
                </div>

                {/* BACK */}
                <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-3xl bg-[#14401F] text-white p-5 flex flex-col justify-between shadow-xl shadow-[#14401F]/30 overflow-hidden">
                  <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-[#B8D958]/15 blur-2xl pointer-events-none" />
                  <div className="relative">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[9px] uppercase tracking-[0.2em] text-[#B8D958] font-bold">
                        {c.sector}
                      </span>
                      <span className="text-[9px] uppercase tracking-[0.2em] text-white/55 font-semibold">
                        {c.stage || "Portfolio"}
                      </span>
                    </div>
                    <div className="font-serif-display text-lg font-bold leading-tight mb-2">
                      {c.name}
                    </div>
                    <p className="text-[11px] leading-relaxed text-white/75">
                      {c.thesis || "Backed by SparkSeed as part of our Tier 2/3 founder portfolio."}
                    </p>
                  </div>
                  <div className="relative flex items-center gap-1.5 text-[10px] text-[#B8D958] font-semibold uppercase tracking-wider">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#B8D958] animate-pulse" />
                    Active Portfolio
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
